import {type RumErrorEvent, type RumEvent} from '@datadog/browser-rum';

import {ignoredErrorMessages} from '../ignoredErrorMessages';
import {
  THIRD_PARTY_DOMAIN_EXCLUSIONS,
  TTI_DOMAIN_INCLUSIONS,
  TTI_WANDB_ANALYTICS_DOMAIN,
} from './profiler/constants';

// https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
const DISCARDED_ERROR_MESSAGES = [
  "'measure' on 'Performance': The mark 'GTM", // Google Tag manager
  'Performance.measure: Given mark name', // Google Tag manager
  'https://jscloud.net/x/25796/inlinks.js',
  ...ignoredErrorMessages,
];

// Third-party sources that aren't denylist entries.
const BROWSER_EXTENSION_SOURCE = 'browser-extension';
const GOOGLE_ADS_SOURCE = 'www.google';

// Extension-injected code uses these URL protocols (Chromium, Firefox,
// Safari).
const BROWSER_EXTENSION_PROTOCOLS = [
  'chrome-extension:',
  'moz-extension:',
  'safari-web-extension:',
] as const;

// Keep the first error per third-party source (tracker script, browser
// extension) as a signal of interference; drop repeats. Never cleared, so
// "first" means first per page load.
const loggedThirdPartyErrorSources = new Set<string>();

/**
 * Central discard filter for the RUM `beforeSend` hook. Returns true when an
 * event should not be sent to Datadog: known-noise error messages, repeat
 * third-party errors, and resource events for third-party trackers, which
 * drown out first-party requests in the resource explorer and session
 * waterfall.
 *
 * @param event - the RUM event passed to `beforeSend`
 * @returns true when the event should be discarded
 */
export function shouldDiscardRumEvent(event: RumEvent): boolean {
  if (event.type === 'error') {
    return (
      DISCARDED_ERROR_MESSAGES.some(msg => event.error.message.includes(msg)) ||
      isRepeatThirdPartyError(event.error)
    );
  }

  if (event.type === 'resource') {
    return isThirdPartyTrackerUrl(event.resource.url);
  }

  return false;
}

/**
 * The third-party source a URL belongs to, or undefined for first-party URLs.
 * A denylist is used instead of a first-party allowlist because customer BYOB
 * storage hosts (arbitrary S3 buckets, `wandb-data.cwobject.com`, ...) are
 * real product traffic with unpredictable hostnames.
 */
function getThirdPartySource(url: string | undefined): string | undefined {
  if (url === undefined) {
    return undefined;
  }
  // Extension-injected code surfaces with an extension protocol and an opaque
  // extension-id host; it's never our traffic.
  if (isBrowserExtensionUrl(url)) {
    return BROWSER_EXTENSION_SOURCE;
  }
  const hostname = getUrlHostname(url);
  if (hostname === undefined) {
    return undefined;
  }
  // Google ad/analytics pings come from www.google.<tld> across ~70 country
  // TLDs; match the prefix instead of listing each one.
  if (hostname.startsWith(`${GOOGLE_ADS_SOURCE}.`)) {
    return GOOGLE_ADS_SOURCE;
  }

  // whether a hostname matches a domain list by exact host or domain
  // suffix — never by substring, so a short entry like `t.co` can't
  // match hosts that contain it (e.g. `gs.wandbusercontent.com`).
  return THIRD_PARTY_DOMAIN_EXCLUSIONS.find(
    domain => hostname === domain || hostname.endsWith(`.${domain}`)
  );
}

function isThirdPartyTrackerUrl(url: string): boolean {
  return getThirdPartySource(url) !== undefined;
}

/**
 * Whether this error is a repeat from a third-party source (see
 * loggedThirdPartyErrorSources). App errors are never discarded here.
 */
function isRepeatThirdPartyError(error: RumErrorEvent['error']): boolean {
  const source = getThirdPartyErrorSource(error);
  if (source === undefined) {
    return false;
  }
  const seen = loggedThirdPartyErrorSources.has(source);
  loggedThirdPartyErrorSources.add(source);
  return seen;
}

/**
 * The third-party source an error originated from, or undefined for app
 * errors. Only the topmost stack frame decides, so an app error that passes
 * through a tracker frame is still the app's. Network-source errors are
 * matched by their failing request URL, since they may carry no stack.
 * Extension frames match anywhere because extensions wrap app code.
 */
function getThirdPartyErrorSource(
  error: RumErrorEvent['error']
): string | undefined {
  return (
    getThirdPartySource(getOriginFrameUrl(error.stack)) ??
    getThirdPartySource(error.resource?.url) ??
    (hasBrowserExtensionFrame(error.stack)
      ? BROWSER_EXTENSION_SOURCE
      : undefined)
  );
}

function isBrowserExtensionUrl(url: string): boolean {
  return BROWSER_EXTENSION_PROTOCOLS.some(protocol => url.startsWith(protocol));
}

function hasBrowserExtensionFrame(stack: string | undefined): boolean {
  return (
    stack !== undefined &&
    BROWSER_EXTENSION_PROTOCOLS.some(protocol => stack.includes(protocol))
  );
}

// Stack frame lines look like "  at fn (<url>)" in Chromium and "fn@<url>" in
// Firefox/Safari, with https or extension-scheme URLs.
const FRAME_LINE_REGEX = /^\s*at\s|@[a-z][\w.+-]*:\/\//i;
const FRAME_URL_REGEX = /[a-z][\w.+-]*:\/\/\S+/i;

/**
 * The URL of the topmost stack frame, or undefined when the stack has no
 * frames. The message line above the frames is skipped since it can embed
 * unrelated URLs.
 */
function getOriginFrameUrl(stack: string | undefined): string | undefined {
  const originFrame = stack
    ?.split('\n')
    .find(line => FRAME_LINE_REGEX.test(line));
  return originFrame?.match(FRAME_URL_REGEX)?.[0];
}

function getUrlHostname(url: string): string | undefined {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return undefined;
  }
}

/**
 * Filter for the RUM `excludedActivityUrls` init option: returns true when a
 * request URL should not count toward the SDK's page-activity / loading-time
 * computation. Only first-party domains (TTI_DOMAIN_INCLUSIONS) count. This is
 * separate from `shouldDiscardRumEvent`, which filters resource *collection*.
 *
 * @param url - the request URL the SDK is evaluating for page activity
 * @returns true when the URL should be excluded from activity tracking
 */
export function shouldExcludeUrlFromPageActivity(url: string): boolean {
  const hostname = getUrlHostname(url);
  return (
    hostname === undefined ||
    // Same exact-host-or-suffix matching as the tracker denylist above.
    !TTI_DOMAIN_INCLUSIONS.some(
      domain => hostname === domain || hostname.endsWith(`.${domain}`)
    ) ||
    // exclude analytics.wandb.ai - this is a special case because it includes wandb.ai, which is allowed
    hostname === TTI_WANDB_ANALYTICS_DOMAIN
  );
}
